import type { FC } from 'react';
import en from 'date-fns/locale/en-GB';
import type { ReactDatePickerProps } from 'react-datepicker';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { styled } from 'styled-components';

import { DATE_FORMAT } from './constants.ts';

// since we handle onChange with different types omit original
// interface DatePickerProps extends Omit<ReactDatePickerProps, 'onChange'>{
export interface DatePickerProps extends ReactDatePickerProps {
  placeholder?: string;
  showLocal?: boolean;
  startOfDay?: boolean;
  dataTestId?: string;
  timeFormat?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

const Wrapper = styled.div`
  .react-datepicker-popper:not(.adnz) {
    .react-datepicker {
      border: none;
      ${({ theme: { shadows } }) => shadows.large};
    }
    .react-datepicker__day--selected,
    .react-datepicker__day--range-end,
    .react-datepicker__day--range-start {
      position: relative;
      background-color: ${({ theme }) => theme.colors.main};
      z-index: 0;
    }

    .react-datepicker__week {
      margin-bottom: 6px;
    }

    .react-datepicker__day--range-start:before,
    .react-datepicker__day--selecting-range-start:before {
      left: 4px;
    }
    .react-datepicker__day--range-end:before {
      right: 4px;
    }

    .react-datepicker__day--in-selecting-range:not(
      .react-datepicker__day--selected,
      .react-datepicker__day--range-end,
      .react-datepicker__day--in-range
    ),
    .react-datepicker__day--in-range:not(.react-datepicker__day--selected, .react-datepicker__day--range-end) {
      background-color: ${({ theme }) => theme.colors.mainLight};
      color: ${({ theme }) => theme.colors.black_3};
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range {
      box-shadow: none;
    }

    .react-datepicker__day--range-start.react-datepicker__day--in-range,
    .react-datepicker__day--keyboard-selected.react-datepicker__day--range-start,
    .react-datepicker__day--keyboard-selected.react-datepicker__day--selecting-range-start {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    .react-datepicker__day--range-end.react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range) {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
    .react-datepicker__day--selecting-range-start {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    .react-datepicker__day--selecting-range-end {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    .react-datepicker__day-name,
    .react-datepicker__day,
    .react-datepicker__time-name {
      margin: 0;
    }
  }
`;

const DatePickerRange: FC<DatePickerProps> = ({
  dateFormat = DATE_FORMAT,
  onChange,
  dataTestId,
  timeFormat,
  startDate,
  endDate,
  ...props
}) => {
  const LOCALE = 'en';

  registerLocale(LOCALE, en);

  return (
    <Wrapper data-testid={dataTestId}>
      <div className='react-datepicker-popper'>
        <ReactDatePicker
          disabledKeyboardNavigation
          locale={LOCALE}
          timeFormat={timeFormat}
          dateFormat={dateFormat}
          selected={startDate}
          onChange={onChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          {...props}
        />
      </div>
    </Wrapper>
  );
};

export { DatePickerRange };
